import type { OperationDto, ProductDto, UnitDto } from "@normax-performance/shared-types";
import type { UnnormedRowState } from "../daily-entry.types";
import { SearchCombobox, Input, type ComboboxOption } from "../../../shared-ui/primitives";
import { formatCodeAndName } from "../../../lib/format";
import type { RowFocusNav } from "./EntryGrid";
import "./NeutralActivityRow.css";
import "./UnnormedWorkRow.css";

export interface UnnormedWorkRowProps {
  index: number;
  row: UnnormedRowState;
  operations: OperationDto[];
  products: ProductDto[];
  units: UnitDto[];
  onChange: (patch: Partial<UnnormedRowState>) => void;
  onDelete: () => void;
  readOnly?: boolean;
  /**
   * Enter-to-next-field registry (task:
   * docs/NORMAX_PERFORMANCE_DAILY_ENTRY_ENTER_KEYBOARD_FLOW_FORENSIC_REPORT.md
   * §9) — real field order: 0 Operacija, 1 Proizvod (opciono), 2 Vreme (min),
   * 3 Količina (opciono), 4 Jedinica, 5 Broj radnog naloga, 6 Napomena.
   * `undefined` in read-only rows.
   */
  focusNav?: RowFocusNav;
  rowRef?: React.Ref<HTMLDivElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
}

/**
 * Product and quantity/unit are OPTIONAL (corrected — DAILY_ENTRY_INTERACTION_SPEC.md
 * §8.8): quantity/unit follow the pair rule (both empty or both filled),
 * enforced by createWorkLineSchema server-side and mirrored here for immediate
 * feedback. No Norma/Planirano/% columns — this type never gets a calculation.
 */
export function UnnormedWorkRow({
  index,
  row,
  operations,
  products,
  units,
  onChange,
  onDelete,
  readOnly,
  focusNav,
  rowRef,
  onKeyDown,
}: UnnormedWorkRowProps) {
  const operationOptions: ComboboxOption[] = operations.map((o) => ({
    id: o.id,
    label: formatCodeAndName(o.code, o.canonicalName),
    businessCode: o.code,
  }));
  const productOptions: ComboboxOption[] = products.map((p) => ({
    id: p.id,
    label: formatCodeAndName(p.code, p.canonicalName),
    businessCode: p.code,
  }));
  const unitOptions: ComboboxOption[] = units.map((u) => ({ id: u.id, label: u.code }));

  const pairError =
    (row.reportedQuantity && !row.unitId) || (!row.reportedQuantity && row.unitId)
      ? "Unesite i količinu i jedinicu, ili ostavite oba polja prazna."
      : null;

  return (
    <div
      className="nx-neutral-row nx-unnormed-row"
      data-row-id={row.localId}
      ref={rowRef}
      onKeyDown={onKeyDown}
    >
      <span className="nx-neutral-row__index tabular-nums">#{index}</span>
      {readOnly ? (
        <span className="nx-neutral-row__ro">
          {operationOptions.find((o) => o.id === row.operationId)?.label}
        </span>
      ) : (
        <div className="nx-neutral-row__field">
          <SearchCombobox
            label="Operacija"
            items={operationOptions}
            selectedKey={row.operationId}
            onSelectionChange={(id) => onChange({ operationId: id })}
            required
            inputRef={focusNav?.field(0).ref}
            onKeyDown={focusNav?.field(0).onKeyDown}
          />
        </div>
      )}
      <div className="nx-neutral-row__field">
        <SearchCombobox
          label="Proizvod (opciono)"
          items={productOptions}
          selectedKey={row.productId}
          onSelectionChange={(id) => onChange({ productId: id })}
          isDisabled={readOnly}
          inputRef={focusNav?.field(1).ref}
          onKeyDown={focusNav?.field(1).onKeyDown}
        />
      </div>
      <div className="nx-neutral-row__field nx-neutral-row__field--num">
        <Input
          label="Vreme (min)"
          inputMode="decimal"
          value={row.durationMinutes}
          disabled={readOnly}
          onChange={(e) => onChange({ durationMinutes: e.target.value })}
          ref={focusNav?.field(2).ref}
          onKeyDown={focusNav?.field(2).onKeyDown}
        />
      </div>
      <div className="nx-neutral-row__field nx-neutral-row__field--num">
        <Input
          label="Količina (opciono)"
          inputMode="decimal"
          value={row.reportedQuantity ?? ""}
          disabled={readOnly}
          error={pairError}
          onChange={(e) => onChange({ reportedQuantity: e.target.value || null })}
          ref={focusNav?.field(3).ref}
          onKeyDown={focusNav?.field(3).onKeyDown}
        />
      </div>
      <div className="nx-neutral-row__field">
        <SearchCombobox
          label="Jedinica"
          items={unitOptions}
          selectedKey={row.unitId}
          onSelectionChange={(id) => onChange({ unitId: id })}
          isDisabled={readOnly}
          inputRef={focusNav?.field(4).ref}
          onKeyDown={focusNav?.field(4).onKeyDown}
        />
      </div>
      <div className="nx-neutral-row__field">
        <Input
          label="Broj radnog naloga"
          value={row.workOrderReference ?? ""}
          disabled={readOnly}
          onChange={(e) => onChange({ workOrderReference: e.target.value })}
          ref={focusNav?.field(5).ref}
          onKeyDown={focusNav?.field(5).onKeyDown}
        />
      </div>
      <div className="nx-neutral-row__field">
        <Input
          label="Napomena"
          value={row.note ?? ""}
          disabled={readOnly}
          onChange={(e) => onChange({ note: e.target.value })}
          ref={focusNav?.field(6).ref}
          onKeyDown={focusNav?.field(6).onKeyDown}
        />
      </div>
      {!readOnly ? (
        <button type="button" className="nx-neutral-row__delete" onClick={onDelete} aria-label="Obriši red">
          🗑
        </button>
      ) : null}
    </div>
  );
}
